type ErrorWithMessage = {
  message: string;
};

function isErrorWithMessage(error: unknown): error is ErrorWithMessage {
  return (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof (error as Record<string, unknown>).message === 'string'
  );
}

function toErrorWithMessage(maybeError: unknown): ErrorWithMessage {
  if (isErrorWithMessage(maybeError)) {
    return maybeError;
  }

  try {
    return new Error(JSON.stringify(maybeError));
  } catch {
    // fallback in case there's an error stringifying the maybeError
    // like with circular references for example.
    return new Error(String(maybeError));
  }
}

export function getErrorMessage(error: unknown) {
  return toErrorWithMessage(error).message;
}

type ResponseErrorLike = {
  response?: { data?: unknown };
};

function hasResponseData(error: unknown): error is ResponseErrorLike {
  return typeof error === 'object' && error !== null && 'response' in error;
}

async function readBlobErrorMessage(blob: Blob): Promise<string | null> {
  try {
    const parsed: unknown = JSON.parse(await blob.text());
    return isErrorWithMessage(parsed) ? parsed.message : null;
  } catch {
    // Not JSON at all — nothing usable to surface.
    return null;
  }
}

/**
 * A request made with `responseType: 'blob'` gets its *error* body as a Blob
 * too, so the server's message ends up unreadable and the user is shown
 * "Request failed with status code 400" instead of why it failed. Re-throws
 * the decoded message when there is one, the original error otherwise.
 *
 * Handles both shapes: the raw AxiosError, and the bare response body the auth
 * interceptor rejects with (`Promise.reject(error.response?.data)`) — which is
 * the Blob itself, and the shape every call under the app's axios instance
 * actually sees.
 */
export async function rethrowWithBlobErrorMessage(
  error: unknown,
): Promise<never> {
  const data =
    error instanceof Blob
      ? error
      : hasResponseData(error)
        ? error.response?.data
        : undefined;

  if (data instanceof Blob) {
    const message = await readBlobErrorMessage(data);
    if (message) {
      throw new Error(message);
    }
  }

  throw error;
}
